import { useState, useEffect, useRef, useLayoutEffect } from 'react';

// Maskowany input daty: użytkownik wpisuje cyfry, kropki dodajemy automatycznie.
// API takie samo jak <input type="date">: wartość w formacie ISO (YYYY-MM-DD),
// onChange dostaje fałszywy event z e.target.value === ISO.
function isoToDmy(iso) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return '';
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function dmyToIso(dmy) {
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(dmy);
  if (!m) return '';
  const dd = m[1].padStart(2, '0');
  const mm = m[2].padStart(2, '0');
  const yyyy = m[3];
  if (Number(dd) < 1 || Number(dd) > 31) return '';
  if (Number(mm) < 1 || Number(mm) > 12) return '';
  return `${yyyy}-${mm}-${dd}`;
}

// Sformatuj surowy input (cyfry + dowolne separatory) jako "dd.mm.rrrr".
// Limit 8 cyfr; kropki wstawiamy po 2. i 4. cyfrze.
function formatDmy(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) out += '.';
    out += digits[i];
  }
  return out;
}

// Policz cyfry w stringu od początku do podanej pozycji.
function digitsBefore(str, pos) {
  let n = 0;
  for (let i = 0; i < Math.min(pos, str.length); i++) {
    if (/\d/.test(str[i])) n++;
  }
  return n;
}

// Znajdź pozycję w sformatowanym stringu po N cyfrach (kursor po-cyfrze).
function positionAfterDigits(formatted, n) {
  if (n <= 0) return 0;
  let count = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      count++;
      if (count === n) return i + 1;
    }
  }
  return formatted.length;
}

function DateField({ value, onChange, type: _ignoredType, ...rest }) {
  const ref = useRef(null);
  const pendingCursor = useRef(null);
  const [text, setText] = useState(isoToDmy(value));

  // Synchronizuj z zewnętrznym value gdy zmieni się spoza komponentu
  // (np. parent reset). Pomijaj, gdy lokalny `text` reprezentuje to samo iso —
  // wpp. nadpisalibyśmy stan w trakcie pisania.
  useEffect(() => {
    const localIso = dmyToIso(text);
    if (localIso === value) return;
    if (text === '' && !value) return;
    setText(isoToDmy(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Po każdym renderze odtwórz pozycję kursora ustaloną w handleChange.
  useLayoutEffect(() => {
    if (pendingCursor.current != null && ref.current) {
      const pos = pendingCursor.current;
      pendingCursor.current = null;
      try { ref.current.setSelectionRange(pos, pos); } catch (e) {}
    }
  });

  const emit = (iso) => {
    if (onChange) onChange({ target: { value: iso } });
  };

  const handleChange = (e) => {
    const rawValue = e.target.value;
    const rawCursor = e.target.selectionStart ?? rawValue.length;
    const dCount = digitsBefore(rawValue, rawCursor);
    const next = formatDmy(rawValue);
    pendingCursor.current = positionAfterDigits(next, dCount);
    setText(next);

    if (next === '') {
      emit('');
      return;
    }
    const iso = dmyToIso(next);
    if (iso) emit(iso);
  };

  const handleBlur = () => {
    const iso = dmyToIso(text);
    if (!iso && text !== '') {
      // Niepoprawna data — wróć do ostatniej znanej dobrej wartości
      setText(isoToDmy(value));
    } else if (iso) {
      emit(iso);
    }
  };

  return (
    <input
      ref={ref}
      className="ds-input"
      type="text"
      inputMode="numeric"
      placeholder="dd.mm.rrrr"
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      maxLength={10}
      {...rest}
    />
  );
}

export function Input({ label, error, className = '', ...props }) {
  const isDate = props.type === 'date';
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
          {label}
        </label>
      )}
      {isDate
        ? <DateField {...props} />
        : <input className="ds-input" {...props} />
      }
      {error && <p style={{ fontSize: '10px', color: 'var(--err2)' }}>{error}</p>}
    </div>
  );
}

export function Select({ label, error, className = '', children, ...props }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
          {label}
        </label>
      )}
      <select className="ds-input" {...props}>{children}</select>
      {error && <p style={{ fontSize: '10px', color: 'var(--err2)' }}>{error}</p>}
    </div>
  );
}

export function Textarea({ label, error, className = '', ...props }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && (
        <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>
          {label}
        </label>
      )}
      <textarea rows={3} className="ds-input resize-none" {...props} />
      {error && <p style={{ fontSize: '10px', color: 'var(--err2)' }}>{error}</p>}
    </div>
  );
}

export function Btn({ variant = 'primary', className = '', children, ...props }) {
  const base = 'inline-flex items-center justify-center rounded-btn font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed';
  const s = {
    primary: { background: 'var(--acc)', color: '#fff', border: '1px solid rgba(184,84,40,0.6)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', padding: '6px 14px' },
    danger:  { background: 'rgba(176,88,72,0.18)', color: 'var(--err2)', border: '1px solid rgba(176,88,72,0.35)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', padding: '6px 14px' },
    ghost:   { background: 'var(--bg4)', color: 'var(--ink2)', border: '1px solid var(--border)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', padding: '6px 14px' },
    success: { background: 'rgba(90,158,114,0.18)', color: 'var(--ok2)', border: '1px solid rgba(90,158,114,0.35)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', padding: '6px 14px' },
  };
  return (
    <button className={`${base} ${className}`} style={s[variant]} {...props}>
      {children}
    </button>
  );
}
